import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface GeolocationCoordinates {
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  /**
   * Obtém a localização atual do usuário usando a API de Geolocalização do navegador
   * @returns Observable com as coordenadas (latitude e longitude)
   */
  getCurrentPosition(): Observable<GeolocationCoordinates> {
    if (!navigator.geolocation) {
      return throwError(() => new Error('Geolocalização não é suportada pelo navegador'));
    }

    return from(
      new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => resolve(position),
          (error) => reject(error),
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      })
    ).pipe(
      map((position: GeolocationPosition) => ({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      })),
      catchError((error: GeolocationPositionError) => {
        let errorMessage = 'Erro ao obter localização';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Permissão de localização negada pelo usuário';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Informações de localização indisponíveis';
            break;
          case error.TIMEOUT:
            errorMessage = 'Tempo esgotado ao tentar obter localização';
            break;
        }
        
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Verifica se a geolocalização está disponível no navegador
   * @returns true se disponível, false caso contrário
   */
  isGeolocationAvailable(): boolean {
    return 'geolocation' in navigator;
  }
}
